import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';
import { SearchableSelect } from '../shared/SearchableSelect';
import type { TransmitterFormData, TransmitterOptions, SuperadminOptions } from '../../types';

interface OrderSpecsSectionProps {
  formData: TransmitterFormData;
  setField: <K extends keyof TransmitterFormData>(key: K, value: TransmitterFormData[K]) => void;
  options: TransmitterOptions;
}

function hasOrganisations(opts: TransmitterOptions): opts is SuperadminOptions {
  return 'organisations' in opts;
}

export function OrderSpecsSection({ formData, setField, options }: OrderSpecsSectionProps) {
  const installedDate = formData.installed_at ? new Date(formData.installed_at) : undefined;

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        Order Specifications
      </h3>

      {/* Organisation */}
      {hasOrganisations(options) && (
        <div className="grid grid-cols-[140px_1fr] items-center gap-2">
          <Label>Organisation</Label>
          <SearchableSelect
            value={formData.organisation}
            onValueChange={(val) => setField('organisation', val)}
            options={options.organisations}
            placeholder="Select organisation"
          />
        </div>
      )}

      {/* Sales Order */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Sales Order</Label>
        <Input
          value={formData.sales_order}
          onChange={(e) => setField('sales_order', e.target.value)}
        />
      </div>

      {/* Quote */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Quote</Label>
        <Input
          value={formData.quote_number}
          onChange={(e) => setField('quote_number', e.target.value)}
        />
      </div>

      {/* Ticket Number */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Ticket Number</Label>
        <Input
          value={formData.ticket_number}
          onChange={(e) => setField('ticket_number', e.target.value)}
        />
      </div>

      {/* Installed At */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Installed</Label>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !formData.installed_at && 'text-muted-foreground',
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.installed_at ? formatDate(formData.installed_at) : 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={installedDate}
                onSelect={(date) =>
                  setField('installed_at', date ? date.toISOString() : null)
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setField('installed_at', new Date().toISOString())}
          >
            Use Current Time
          </Button>
        </div>
      </div>
    </div>
  );
}
